import { useSelector } from "react-redux";
import type { RootState } from "../redux/store/store";
import { useAppDispatch } from "../redux/store/hooks";
import { navigate, selectModule } from "../redux/slice/layoutSlice";
import { builtInModules, getModule } from "../config/module-config";
import type { XMenuItem, XModule, XRoute } from "../config/module-types";
import { screenRegistry } from "../screens/registry";
import { Placeholder } from "../screens/Placeholder";
import { useT } from "../i18n";
import { AppBar } from "./AppBar";
import { Menu } from "./Menu";

const stripQuery = (path: string) => path.split("?")[0];

const flattenMenu = (items: XMenuItem[]): XMenuItem[] =>
  items.flatMap((item) =>
    item.children?.length ? [item, ...item.children] : [item],
  );

// All routes across modules, tagged with their owning module.
const allRoutes = builtInModules.flatMap((m: XModule) =>
  m.routes.map((r: XRoute) => ({ ...r, moduleId: m.id })),
);

/**
 * Application shell — AppBar + collapsible sidebar Menu + content area, with a
 * breadcrumb. Routing is config-driven via Redux state (no router dependency):
 * the current path is matched against module routes and rendered through the
 * screen registry, falling back to a Placeholder.
 */
export const Layout = () => {
  const dispatch = useAppDispatch();
  const { activeModuleId, currentPath, navSeq, breadcrumbDetail } = useSelector(
    (s: RootState) => s.layout,
  );
  const activeModule = getModule(activeModuleId);
  const t = useT();

  // Prefer a route owned by the active module, else any module's match.
  const candidateRoutes = allRoutes.filter(
    (r) => stripQuery(r.path) === currentPath,
  );
  const route =
    candidateRoutes.find((r) => r.moduleId === activeModuleId) ??
    candidateRoutes[0];

  // Menu item (for icon + label fallback) — search the active module first.
  const menuItem =
    flattenMenu(activeModule?.menu ?? []).find(
      (i) => stripQuery(i.path) === currentPath,
    ) ??
    builtInModules
      .flatMap((m) => flattenMenu(m.menu))
      .find((i) => stripQuery(i.path) === currentPath);

  // Same `nav.<id>` key space the sidebar uses (see Menu.tsx), so a page's
  // breadcrumb and its menu entry always read the same. The English title from
  // module-config is the fallback for a page with no entry yet.
  const englishTitle = route?.title ?? menuItem?.label;
  const navId = route?.id ?? menuItem?.id;
  const title =
    navId && englishTitle
      ? t(`nav.${navId}`, englishTitle)
      : (englishTitle ?? t("appbar.pageNotFound"));

  const Screen = route?.screen ? screenRegistry[route.screen] : undefined;

  return (
    <div className="shell">
      <AppBar />
      <div className="shell__body">
        <Menu />
        <main className="shell__main">
          <div className="breadcrumb">
            <button
              type="button"
              className="breadcrumb__module breadcrumb__link"
              onClick={() => dispatch(selectModule(activeModuleId))}
            >
              {activeModule ? t(`module.${activeModule.id}`, activeModule.name) : null}
            </button>
            <span className="breadcrumb__sep">/</span>
            <button
              type="button"
              className="breadcrumb__page breadcrumb__link"
              onClick={() => dispatch(navigate(currentPath))}
            >
              {title}
            </button>
            {breadcrumbDetail ? (
              <>
                <span className="breadcrumb__sep">/</span>
                <span className="breadcrumb__page">{breadcrumbDetail}</span>
              </>
            ) : null}
          </div>
          <div className="shell__content">
            {Screen ? (
              // Keyed by navSeq so re-clicking the already-active nav item
              // remounts the screen — otherwise a screen with its own
              // internal "sub view" state (e.g. an inventory overview) never
              // resets, since the component instance wouldn't otherwise
              // change.
              <Screen key={`${route?.screen}-${navSeq}`} />
            ) : (
              <Placeholder
                title={title}
                pageType={route?.pageType}
                icon={menuItem?.icon}
              />
            )}
          </div>
        </main>
      </div>
    </div>
  );
};
